import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Send, Users, Building2, Layers, Settings, Percent } from 'lucide-react';
import { BRAND } from '@/constants/brand';
import { useAuth } from '@/context/AuthContext';
import { useData } from '@/context/DataContext';
import { useQuoteCalc } from '@/hooks/useQuoteCalc';
import { createEmptyQuote } from '@/lib/quoteFactory';
import StepCliente from '@/components/wizard/steps/StepCliente';
import StepProyecto from '@/components/wizard/steps/StepProyecto';
import StepProductos from '@/components/wizard/steps/StepProductos';
import StepConfig from '@/components/wizard/steps/StepConfig';
import StepDescuentos from '@/components/wizard/steps/StepDescuentos';
import { normalizeQuote } from '@/lib/normalizeQuote';

const STEPS = [
  { num: 1, label: 'Cliente', icon: Users },
  { num: 2, label: 'Proyecto', icon: Building2 },
  { num: 3, label: 'Productos', icon: Layers },
  { num: 4, label: 'Configuración', icon: Settings },
  { num: 5, label: 'Descuentos y Pago', icon: Percent },
];

export default function QuoteWizard() {
  const { user } = useAuth();
  const { quotes, discountCodes, saveQuote, nextFolio } = useData();
  const navigate = useNavigate();
  const { id } = useParams();

  const existing = id ? quotes.find((q) => q.id === id) : null;
  const [step, setStep] = useState(1);  

  const [quote, setQuote] = useState(normalizeQuote(existing) || createEmptyQuote(user));

  const [saving, setSaving] = useState(false);

  // Si llega un id pero el quote aún no se cargó (race con DataProvider)
  useEffect(() => {
    if (id && existing && quote.id !== existing.id) {
      setQuote(normalizeQuote(existing));
    }
  }, [id, existing, quote.id]);

  useQuoteCalc(quote, setQuote);

  const canAdvance = () => {
    if (step === 1)
      return quote.cliente.nombre && quote.cliente.empresa && quote.cliente.email;
    if (step === 2) return quote.proyecto.nombre;
    if (step === 3) return quote.productos.length > 0;
    if (step === 4) return quote.productos.every((p) => p.configurado);
    return true;
  };

const handleSave = async (estatus = 'borrador') => {
  if (saving) return;
  setSaving(true);
  try {
    let folio = quote.folio;
    if (!folio) folio = await nextFolio();

    const quoteToSave = {
      ...quote,
      folio,
      estatus,
      fechaActualizacion: new Date().toISOString(),
    };

    if (!quote.id || quote.id.startsWith('quote-')) {
      delete quoteToSave.id;
    }

    const saved = await saveQuote(quoteToSave);
    setQuote((prev) => ({ ...prev, id: saved.id, folio: saved.folio }));

    if (estatus === 'enviada') {
      navigate(`/cotizacion/${saved.id}`);
    }
  } finally {
    setSaving(false);
  }
};


  return (
    <div className="flex-1 overflow-auto bg-neutral-950">
      <div className="max-w-5xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-neutral-500 hover:text-white mb-2 flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" /> Volver
            </button>
            <h1 className="text-3xl font-bold text-white">
              {existing ? 'Editar cotización' : 'Nueva cotización'}
            </h1>
          </div>
          <button
            onClick={() => handleSave('borrador')}
            disabled={saving}
            className="px-4 py-2 text-sm text-neutral-400 hover:text-white border border-neutral-800 hover:border-neutral-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar borrador'}
          </button>
        </div>

        <div className="mb-10">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const active = step === s.num;
              const completed = step > s.num;
              return (
                <div key={s.num} className="flex items-center flex-1">
                  <button
                    onClick={() => {
                      if (completed || active) setStep(s.num);
                    }}
                    className="flex flex-col items-center gap-2"
                    disabled={!completed && !active}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                        active
                          ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                          : completed
                          ? 'border-green-500 bg-green-500/10 text-green-500'
                          : 'border-neutral-800 bg-neutral-900 text-neutral-600'
                      }`}
                    >
                      {completed ? <Check className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div
                      className={`text-xs font-medium ${
                        active
                          ? 'text-white'
                          : completed
                          ? 'text-neutral-400'
                          : 'text-neutral-600'
                      }`}
                    >
                      {s.label}
                    </div>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-px mx-2 ${
                        completed ? 'bg-green-500/50' : 'bg-neutral-800'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-8 mb-6">
          {step === 1 && <StepCliente quote={quote} setQuote={setQuote} />}
          {step === 2 && <StepProyecto quote={quote} setQuote={setQuote} />}
          {step === 3 && <StepProductos quote={quote} setQuote={setQuote} />}
          {step === 4 && <StepConfig quote={quote} setQuote={setQuote} />}
          {step === 5 && (
            <StepDescuentos
              quote={quote}
              setQuote={setQuote}
              discountCodes={discountCodes}
            />
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="flex items-center gap-2 px-5 py-2.5 text-sm text-neutral-400 hover:text-white border border-neutral-800 hover:border-neutral-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <div className="text-sm text-neutral-500">Paso {step} de 5</div>
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:scale-[1.02] transition-transform"
              style={{ background: BRAND.gold, color: BRAND.black }}
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => handleSave('enviada')}
              disabled={!canAdvance()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium disabled:opacity-30 hover:scale-[1.02] transition-transform"
              style={{ background: BRAND.gold, color: BRAND.black }}
            >
              Generar propuesta <Send className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
